import React from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Grid, TextField, Checkbox, FormControlLabel, Select, MenuItem, Box, Paper } from '@mui/material';

type CustomerSettingsListProps = {
    data: { [key: string]: any[] };
    handleValueChange: (category: string, name: string, value: any) => void;
    validationErrors: Record<string, string>;
    getDisplayValue: (val: any) => any;
    formatValue: (val: any) => any;
};

export const CustomerSettingsList: React.FC<CustomerSettingsListProps> = ({
    data,
    handleValueChange,
    validationErrors,
    getDisplayValue,
    formatValue,
}) => {
    const { t: translate } = useTranslation(['customer-settings', 'common']);

    return (
        <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            {/* Header */}
            <Grid
                container
                sx={{
                    bgcolor: '#1565c0',
                    color: 'white',
                    px: 2,
                    py: 1.25,
                    fontWeight: 600,
                    alignItems: 'center',
                }}
            >
                <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">{translate('customer-settings:SETTING')}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                    <Typography variant="subtitle2">{translate('customer-settings:DESCRIPTION')}</Typography>
                </Grid>
                <Grid item xs={12} sm={2}>
                    <Typography variant="subtitle2">{translate('customer-settings:DEFAULT_VALUE')}</Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                    <Typography variant="subtitle2">{translate('customer-settings:SET_VALUE')}</Typography>
                </Grid>
            </Grid>

            {/* Body */}
            {Object.keys(data).map((category) => (
                <Box key={category}>
                    <Box
                        sx={{
                            bgcolor: '#f3f4f6',
                            px: 2,
                            py: 1,
                            borderTop: '1px solid #e5e7eb',
                            fontWeight: 600,
                            color: '#374151',
                        }}
                    >
                        {/* {category} */}
                        {translate(`customer-settings:${category.toUpperCase().replace(/ /g, '_')}`)}
                    </Box>

                    {data[category].map((setting: any) => (
                        <Grid
                            key={setting.name}
                            container
                            alignItems="flex-start"
                            sx={{
                                px: 2,
                                py: 1.25,
                                borderTop: '1px solid #e5e7eb',
                            }}
                        >
                            <Grid item xs={12} sm={3} sx={{ pr: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 500,
                                        wordBreak: 'break-word',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {/* {setting.name} */}
                                    {translate(`customer-settings:${setting.name.toUpperCase().replace(/ /g, '_')}`)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={4} sx={{ pr: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{
                                        wordBreak: 'break-word',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {translate(
                                        `customer-settings:${setting.name.toUpperCase().replace(/ /g, '_')}_DESCRIPTION`,
                                    ) || setting.description}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={2} sx={{ pr: 1, minWidth: 0 }}>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        wordBreak: 'break-word',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                    }}
                                >
                                    {formatValue(setting.defaultValue)}
                                </Typography>
                            </Grid>

                            <Grid item xs={12} sm={3} sx={{ minWidth: 0 }}>
                                {(setting.type === 'String' || setting.type === 'Password') && (
                                    <TextField
                                        fullWidth
                                        type={setting.type === 'Password' ? 'password' : 'text'}
                                        size="small"
                                        data-field={setting.name}
                                        value={getDisplayValue(setting.preSelectedValue)}
                                        error={!!validationErrors[setting.name]}
                                        helperText={
                                            validationErrors[setting.name] &&
                                            translate(`customer-settings:${validationErrors[setting.name]}`)
                                        }
                                        onChange={(e) => handleValueChange(category, setting.name, e.target.value)}
                                    />
                                )}
                                {setting.type === 'Number' && (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="number"
                                        inputProps={{ min: 0 }}
                                        data-field={setting.name}
                                        value={getDisplayValue(setting.preSelectedValue)}
                                        error={!!validationErrors[setting.name]}
                                        helperText={
                                            validationErrors[setting.name] &&
                                            translate(`customer-settings:${validationErrors[setting.name]}`)
                                        }
                                        onChange={(e) => {
                                            const value = parseFloat(e.target.value);
                                            if (!isNaN(value) && value >= 0) {
                                                handleValueChange(category, setting.name, e.target.value);
                                            } else if (e.target.value === '') {
                                                handleValueChange(category, setting.name, '');
                                            }
                                        }}
                                    />
                                )}
                                {(setting.type === 'Text' || setting.type === 'Object') && (
                                    <TextField
                                        fullWidth
                                        size="small"
                                        multiline
                                        minRows={3}
                                        data-field={setting.name}
                                        value={getDisplayValue(setting.preSelectedValue)}
                                        error={!!validationErrors[setting.name]}
                                        helperText={
                                            validationErrors[setting.name] &&
                                            translate(`customer-settings:${validationErrors[setting.name]}`)
                                        }
                                        onChange={(e) => handleValueChange(category, setting.name, e.target.value)}
                                    />
                                )}
                                {setting.type === 'List' && (
                                    <Select
                                        fullWidth
                                        size="small"
                                        data-field={setting.name}
                                        error={!!validationErrors[setting.name]}
                                        value={getDisplayValue(setting.preSelectedValue)}
                                        onChange={(e) => handleValueChange(category, setting.name, e.target.value)}
                                    >
                                        {setting.values?.map((v: any) => (
                                            <MenuItem key={v} value={v}>
                                                {v}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                )}
                                {setting.type === 'List' && validationErrors[setting.name] && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                                        {validationErrors[setting.name]}
                                    </Typography>
                                )}
                                {setting.type === 'Boolean' && (
                                    <Box data-field={setting.name}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        setting.preSelectedValue === true ||
                                                        setting.preSelectedValue === 'true'
                                                    }
                                                    onChange={(e) =>
                                                        handleValueChange(category, setting.name, e.target.checked)
                                                    }
                                                />
                                            }
                                            label=""
                                        />
                                        {validationErrors[setting.name] && (
                                            <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                                                {validationErrors[setting.name]}
                                            </Typography>
                                        )}
                                    </Box>
                                )}
                            </Grid>
                        </Grid>
                    ))}
                </Box>
            ))}
        </Paper>
    );
};
